import React, { useState } from "react";
import { Button, Modal, Tooltip } from "antd";
import { useIntl } from "react-intl";
import { DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { SQUARE_CARD_BRANDS } from "constants/PaymentConstant";
import Amex from "assets/cards/Amex.svg";
import Discover from "assets/cards/Discover.svg";
import Mastercard from "assets/cards/Mastercard.svg";
import Visa from "assets/cards/Visa.svg";
import Diners from "assets/cards/Diners.svg";
import UnionPay from "assets/cards/UnionPay.svg";
import JCB from "assets/cards/JCB.svg";
import translations from "./translations";
import { CardChipIcon } from "assets/svg/membershipicons";

const { confirm } = Modal;

const CreditCardsDisplay = ({
  card,
  onDelete,
  includeDelete = false,
  onCardClick,
  className = "",
  style = {},
  selected = false,
}) => {
  const { formatMessage } = useIntl();
  const [isHovered, setIsHovered] = useState(false);
  const canDelete = !card.subscription && includeDelete;
  const showDeleteOverlay = includeDelete && isHovered;

  const brand_img_map = {
    [SQUARE_CARD_BRANDS.AMERICAN_EXPRESS]: Amex,
    [SQUARE_CARD_BRANDS.AMERICAN_EXPRESS_FULL]: Amex,
    [SQUARE_CARD_BRANDS.DISCOVER]: Discover,
    [SQUARE_CARD_BRANDS.MASTERCARD]: Mastercard,
    [SQUARE_CARD_BRANDS.VISA]: Visa,
    [SQUARE_CARD_BRANDS.DISCOVER_DINERS]: Diners,
    [SQUARE_CARD_BRANDS.UNION_PAY]: UnionPay,
    [SQUARE_CARD_BRANDS.JCB]: JCB,
  };

  const cardBrand = card?.brand?.toLowerCase();

  const brandClassMap = {
    [SQUARE_CARD_BRANDS.VISA]: "visa",
    [SQUARE_CARD_BRANDS.MASTERCARD]: "mastercard",
    [SQUARE_CARD_BRANDS.AMERICAN_EXPRESS]: "american_express",
    [SQUARE_CARD_BRANDS.AMERICAN_EXPRESS_FULL]: Amex,
    [SQUARE_CARD_BRANDS.DISCOVER]: "discover",
    [SQUARE_CARD_BRANDS.DISCOVER_DINERS]: "discover_diners",
    [SQUARE_CARD_BRANDS.UNION_PAY]: "china_unionpay",
    [SQUARE_CARD_BRANDS.JCB]: "jcb",
  };

  const brandClass = brandClassMap[cardBrand] || "default";
  const cursorClass = onCardClick ? "clickable" : "default-cursor";
  const selectedClass = selected ? "selected" : "";

  const handleDeleteClick = (e) => {
    e.stopPropagation();

    if (!canDelete) {
      Modal.warning({
        title: formatMessage(translations.cannotDeleteCardTitle),
        content: formatMessage(translations.cannotDeleteCardContent),
        okText: formatMessage(translations.understood),
      });
      return;
    }

    confirm({
      title: formatMessage(translations.deleteCreditCardTitle),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage(translations.deleteCreditCardContent, {
        lastDigits: card.last_digits,
      }),
      okText: formatMessage(translations.delete),
      okType: "danger",
      cancelText: formatMessage(translations.cancel),
      onOk() {
        onDelete?.(card.id);
      },
    });
  };

  const handleCardClick = () => {
    onCardClick?.(card);
  };

  return (
    <div
      className={`credit-card-container ${brandClass} ${cursorClass} ${selectedClass} ${className}`}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      {showDeleteOverlay && (
        <div className="credit-card-delete-overlay">
          {!canDelete ? (
            <Tooltip title={formatMessage(translations.deleteTooltip)}>
              <Button
                type="primary"
                disabled={true}
                icon={<DeleteOutlined />}
                size="middle"
                onClick={handleDeleteClick}
              >
                {formatMessage(translations.cannotDelete)}
              </Button>
            </Tooltip>
          ) : (
            <Button
              icon={<DeleteOutlined />}
              size="middle"
              onClick={handleDeleteClick}
              className="regular-btn"
            >
              {formatMessage(translations.deleteCard)}
            </Button>
          )}
        </div>
      )}

      <div className="credit-card-chip">
        <CardChipIcon />
      </div>

      <div className="credit-card-logo">
        <img alt={card?.brand} src={brand_img_map[cardBrand]} />
      </div>

      <div className="credit-card-number">
        {formatMessage(translations.numberPlaceholder, {
          lastDigits: card.last_digits,
        })}
      </div>

      <div className="credit-card-bottom-row">
        <div className="credit-card-holder">
          <div className="label">{formatMessage(translations.cardHolder)}</div>
          <div className="value">{card.billing_name}</div>
        </div>
        <div className="credit-card-expiry">
          <div className="label">{formatMessage(translations.validThru)}</div>
          <div className="value">
            {String(card.expire_month).padStart(2, "0")}/
            {card.expire_year?.toString()?.slice(2, 4)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditCardsDisplay;
